// ============================================================
// templatesPage.js — Página "Plantillas de vídeo"
// ------------------------------------------------------------
// - Abre/cierra la biblioteca de plantillas en el panel derecho
// - Al seleccionar una plantilla, la "sube" al card de vídeo de
//   la izquierda y muestra el toggle de Audio
// - Maneja Historial (placeholder) y el botón Generar
// ============================================================
import { qs, qsa, on } from './utils.js';
import { toast } from './toast.js';

export function initTemplatesPage() {
  // Solo aplica en la página de plantillas
  if (document.body.dataset.page !== 'plantillas') return;

  const stage = qs('.templates-stage');
  const resultsView = qs('[data-view="results"]');
  const libraryView = qs('[data-view="library"]');
  const audioToggleWrap = qs('#audioToggleWrap');
  const videoCard = qs('#uploadCardVideo');
  const imageCard = qs('#uploadCardImage');

  if (!stage) return;

  // ---------- Abrir / cerrar biblioteca ----------
  on(document, 'click', '[data-action="open-library"]', () => {
    resultsView?.classList.add('is-hidden');
    libraryView?.classList.remove('is-hidden');
  });

  on(document, 'click', '[data-action="close-library"]', () => {
    libraryView?.classList.add('is-hidden');
    resultsView?.classList.remove('is-hidden');
  });

  // ---------- Seleccionar plantilla ----------
  // La biblioteca se mantiene abierta — la selección queda marcada en lila
  // hasta que el usuario elija otra o cierre la biblioteca con la X.
  on(stage, 'click', '.library-card', (e, card) => {
    qsa('.library-card', stage).forEach((c) => c.classList.remove('is-selected'));
    card.classList.add('is-selected');

    const thumb = card.dataset.thumb;
    const title = qs('.library-card-title', card)?.textContent.trim() || 'Plantilla';

    fillUploadCard(videoCard, thumb, title);
    audioToggleWrap?.classList.remove('is-hidden');

    toast({ message: `Plantilla "${title}" cargada`, type: 'success' });
  });

  // ---------- Popover de calidad + nº de salidas ----------
  on(document, 'click', '.quality-tile', (e, tile) => {
    const group = tile.parentElement?.dataset.group;
    if (!group) return;

    qsa(`[data-group="${group}"] .quality-tile`).forEach((t) => t.classList.remove('is-active'));
    tile.classList.add('is-active');

    const value = tile.dataset.value;
    const target = qs(`[data-bind="${group}"]`);
    if (target) target.textContent = value;
  });

  // ---------- Quitar contenido de un card de subida ----------
  on(document, 'click', '[data-action="clear-upload"]', (e, btn) => {
    const card = btn.closest('.upload-card');
    if (!card) return;
    clearUploadCard(card);

    // Si era el card de vídeo, desmarcar la plantilla seleccionada
    if (card === videoCard) {
      qsa('.library-card.is-selected', stage).forEach((c) => c.classList.remove('is-selected'));
    }

    // Si los dos cards quedan vacíos, ocultar el toggle de audio
    if (!videoCard.classList.contains('has-content') &&
        !imageCard.classList.contains('has-content')) {
      audioToggleWrap?.classList.add('is-hidden');
    }
  });

  // ---------- Historial (placeholder) ----------
  on(document, 'click', '[data-action="open-history"]', () => {
    toast({ message: 'Aquí se abrirá tu historial de vídeos', type: 'info' });
  });

  on(document, 'click', '[data-action="open-image-history"]', () => {
    toast({ message: 'Aquí se abrirá tu historial de imágenes', type: 'info' });
  });

  // ---------- Subida de imagen del personaje (file picker) ----------
  imageCard?.addEventListener('click', (e) => {
    // Solo abre el picker si se clica el área vacía, no las acciones internas
    if (e.target.closest('.upload-card-actions')) return;
    if (e.target.closest('[data-action="clear-upload"]')) return;
    if (imageCard.classList.contains('has-content')) return;
    openImagePicker(imageCard);
  });

  // ---------- Cambio de opción de orientación ----------
  qsa('.orientation-option input[name="orientation"]').forEach((input) => {
    input.addEventListener('change', () => {
      qsa('.orientation-option').forEach((opt) => opt.classList.remove('is-active'));
      input.closest('.orientation-option')?.classList.add('is-active');
    });
  });

  // ---------- Generar ----------
  on(document, 'click', '[data-action="generate-template"]', () => {
    if (!videoCard.classList.contains('has-content')) {
      toast({ message: 'Selecciona primero una plantilla de vídeo', type: 'error' });
      return;
    }
    if (!imageCard.classList.contains('has-content')) {
      toast({ message: 'Añade una imagen del personaje', type: 'error' });
      return;
    }
    toast({ message: 'Generando vídeo con la plantilla seleccionada…', type: 'success' });
  });
}

// ---------- Helpers ----------
function fillUploadCard(card, imageUrl, label) {
  if (!card) return;
  const empty = qs('.upload-card-empty', card);
  const filled = qs('.upload-card-filled', card);
  const thumb = qs('[data-bind="upload-thumb"]', card);

  if (thumb && imageUrl) {
    thumb.style.background = `center / cover no-repeat url("${imageUrl}")`;
  }
  empty?.classList.add('is-hidden');
  filled?.classList.remove('is-hidden');
  card.classList.add('has-content');
  card.dataset.label = label || '';
}

function clearUploadCard(card) {
  const empty = qs('.upload-card-empty', card);
  const filled = qs('.upload-card-filled', card);
  const thumb = qs('[data-bind="upload-thumb"]', card);

  if (thumb) thumb.style.background = '';
  filled?.classList.add('is-hidden');
  empty?.classList.remove('is-hidden');
  card.classList.remove('has-content');
  delete card.dataset.label;
}

function openImagePicker(card) {
  const input = document.createElement('input');
  input.type = 'file';
  input.accept = 'image/*';
  input.addEventListener('change', (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      fillUploadCard(card, reader.result, file.name);
      toast({ message: `Imagen "${file.name}" añadida`, type: 'success' });
      qs('#audioToggleWrap')?.classList.remove('is-hidden');
    };
    reader.readAsDataURL(file);
  });
  input.click();
}
